import type { PanelPlugin, PluginContext } from '../core/plugin'
import type { MessageBridge } from './message-bridge'
import type { HistoryManager } from './history'
import { CONFIG, type FeatureConfig } from './config'
import { ICONS } from './icons'
import { MATHLIVE_ZH_HANS_STRINGS } from './mathlive-i18n'
import { log, isMathLiveEvent } from './utils'

declare const MathJax: any

interface AIFormulaResultPayload {
  success?: boolean,
  content?: string,
  elementId?: string,
}

type FormulaMode = 'default' | 'manual'

const PANEL_VIEWPORT_MARGIN = 10
const FORMULA_KEYBOARD_GAP = 12
const KEYBOARD_REPOSITION_DELAY_MS = 80

export class FormulaManager implements PanelPlugin {
  readonly name = 'formula'

  private bridge: MessageBridge

  private history: HistoryManager

  private featureConfig: FeatureConfig

  private panel: HTMLDivElement | null = null

  private _mathField: HTMLElement | null = null

  private activeElement: HTMLElement | null = null

  private mathJaxReady = false

  private mathLiveLoaded = false

  private mathLiveLoading = false

  private isVisible_ = false

  private _scrollHandler: (() => void) | null = null

  private _currentMode: FormulaMode = 'default'

  private _originalLatex = ''

  private _pendingFormulaContent: string | null = null

  // AI 生公式"等待中的目标 elementId"。发请求时记录；切换/隐藏面板时清空。
  // 结果到达时若为 null 或与当前不匹配，则丢弃，避免错位替换。
  private _pendingAiTargetElementId: string | null = null

  private _lastFormulaPrompt = ''

  private _vkOpen = false

  get visible(): boolean {
    return this.isVisible_
  }

  constructor(messageBridge: MessageBridge, historyManager: HistoryManager, featureConfig: FeatureConfig) {
    this.bridge = messageBridge
    this.history = historyManager
    this.featureConfig = featureConfig
  }

  create(_ctx?: PluginContext): void {
    this._createPanel()
  }

  async init(): Promise<boolean> {
    if (!(window as any).MathJax || !(window as any).MathJax.startup) {
      log('页面未加载 MathJax，跳过公式功能')
      return false
    }
    try {
      await MathJax.startup.promise
      this.mathJaxReady = true
      this.scanFormulas()
      return true
    } catch (e) {
      log('MathJax 初始化等待失败', e)
      return false
    }
  }

  scanFormulas(): number {
    if (!this.mathJaxReady) return 0
    let count = 0
    try {
      const mathDoc = MathJax.startup.document
      if (mathDoc && mathDoc.math) {
        for (const item of mathDoc.math) {
          if (item.typesetRoot) {
            item.typesetRoot.setAttribute('data-musk-formula', 'true')
            item.typesetRoot.setAttribute('data-musk-latex', item.math)
            item.typesetRoot.setAttribute('data-musk-display', item.display ? 'true' : 'false')
            if (!item.typesetRoot.hasAttribute('data-musk-element-id')) {
              item.typesetRoot.setAttribute('data-musk-element-id',
                `musk-edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`)
            }
            count++
          }
        }
      }
    } catch (e) {
      log('MathJax math 迭代失败，尝试备用方案', e)
      count = this._scanFallback()
    }
    log('公式扫描完成', { count })
    return count
  }

  private _getMathMLElement(container: Element): Element | null {
    return container.querySelector('mjx-assistive-mml math') || container.querySelector('math')
  }

  private _mathmlToLatex(el: Node): string {
    if (!el) return ''
    if (el.nodeType === 3) return (el.textContent || '').trim()
    const elem = el as Element
    const tag = elem.tagName ? elem.tagName.toLowerCase().replace(/^.*:/, '') : ''
    const kids = Array.from(el.childNodes).filter((n) => n.nodeType === 1 || (n.nodeType === 3 && (n.textContent || '').trim()))
    const self = this
    const childLatex = function (): string { return kids.map((k) => self._mathmlToLatex(k)).join('') }
    const child = function (i: number): string { return kids[i] ? self._mathmlToLatex(kids[i]) : '' }

    switch (tag) {
      case 'math':
      case 'mrow':
      case 'mstyle':
      case 'mpadded':
      case 'semantics':
        return childLatex()
      case 'mi': {
        const txt = (el.textContent || '').trim()
        const greekMap: Record<string, string> = {
          α: '\\alpha',
          β: '\\beta',
          γ: '\\gamma',
          δ: '\\delta',
          ε: '\\epsilon',
          ζ: '\\zeta',
          η: '\\eta',
          θ: '\\theta',
          ι: '\\iota',
          κ: '\\kappa',
          λ: '\\lambda',
          μ: '\\mu',
          ν: '\\nu',
          ξ: '\\xi',
          π: '\\pi',
          ρ: '\\rho',
          σ: '\\sigma',
          τ: '\\tau',
          υ: '\\upsilon',
          φ: '\\phi',
          χ: '\\chi',
          ψ: '\\psi',
          ω: '\\omega',
          Γ: '\\Gamma',
          Δ: '\\Delta',
          Θ: '\\Theta',
          Λ: '\\Lambda',
          Ξ: '\\Xi',
          Π: '\\Pi',
          Σ: '\\Sigma',
          Φ: '\\Phi',
          Ψ: '\\Psi',
          Ω: '\\Omega',
          ϕ: '\\phi',
          ϵ: '\\epsilon',
          ϰ: '\\varkappa',
        }
        if (greekMap[txt]) return `${greekMap[txt]} `
        const funcNames = ['sin', 'cos', 'tan', 'cot', 'sec', 'csc', 'arcsin', 'arccos', 'arctan',
          'sinh', 'cosh', 'tanh', 'log', 'ln', 'exp', 'lim', 'max', 'min', 'sup', 'inf',
          'det', 'dim', 'gcd', 'deg', 'hom', 'ker', 'arg']
        if (funcNames.indexOf(txt) >= 0) return `\\${txt} `
        return txt
      }
      case 'mn':
        return (el.textContent || '').trim()
      case 'mo': {
        const op = (el.textContent || '').trim()
        const opMap: Record<string, string> = {
          '⁡': '',
          '⁢': '',
          '⁣': '',
          '⁤': '',
          '±': '\\pm ',
          '∓': '\\mp ',
          '×': '\\times ',
          '÷': '\\div ',
          '≤': '\\le ',
          '≥': '\\ge ',
          '≠': '\\neq ',
          '≈': '\\approx ',
          '∞': '\\infty ',
          '∑': '\\sum ',
          '∏': '\\prod ',
          '∫': '\\int ',
          '∂': '\\partial ',
          '∇': '\\nabla ',
          '→': '\\to ',
          '←': '\\leftarrow ',
          '⇒': '\\Rightarrow ',
          '⇔': '\\Leftrightarrow ',
          '∈': '\\in ',
          '∉': '\\notin ',
          '⊂': '\\subset ',
          '⊆': '\\subseteq ',
          '∪': '\\cup ',
          '∩': '\\cap ',
          '∅': '\\emptyset ',
          '⋅': '\\cdot ',
          '…': '\\ldots ',
          '⋯': '\\cdots ',
          '∨': '\\lor ',
          '∧': '\\land ',
          '¬': '\\neg ',
        }
        if (opMap[op] !== undefined) return opMap[op]
        return op
      }
      case 'mfrac':
        return `\\frac{${child(0)}}{${child(1)}}`
      case 'msup':
        return `${child(0)}^{${child(1)}}`
      case 'msub':
        return `${child(0)}_{${child(1)}}`
      case 'msubsup':
        return `${child(0)}_{${child(1)}}^{${child(2)}}`
      case 'msqrt':
        return `\\sqrt{${childLatex()}}`
      case 'mroot':
        return `\\sqrt[${child(1)}]{${child(0)}}`
      case 'mover': {
        const acc = (kids[1] && kids[1].textContent || '').trim()
        const accMap: Record<string, string> = {
          '¯': '\\overline',
          '̂': '\\hat',
          '̃': '\\tilde',
          '̇': '\\dot',
          '⃗': '\\vec',
          '˜': '\\tilde',
          '^': '\\hat',
          '→': '\\overrightarrow',
        }
        if (accMap[acc]) return `${accMap[acc]}{${child(0)}}`
        return `\\overset{${child(1)}}{${child(0)}}`
      }
      case 'munder':
        return `\\underset{${child(1)}}{${child(0)}}`
      case 'munderover':
        return `${child(0)}_{${child(1)}}^{${child(2)}}`
      case 'mtext': {
        const t = (el.textContent || '').trim()
        return t ? `\\text{${t}}` : ''
      }
      case 'mspace':
        return '\\; '
      case 'mtable': {
        const rows = kids.map((r) => self._mathmlToLatex(r))
        return `\\begin{matrix}${rows.join(' \\\\ ')}\\end{matrix}`
      }
      case 'mtr':
        return kids.map((c) => self._mathmlToLatex(c)).join(' & ')
      case 'mtd':
        return childLatex()
      case 'annotation':
        if (elem.getAttribute('encoding') === 'application/x-tex') return (el.textContent || '').trim()
        return ''
      default:
        return childLatex()
    }
  }

  private _extractLatex(container: Element): string {
    const mathEl = this._getMathMLElement(container)
    if (!mathEl) return ''
    const ann = mathEl.querySelector('annotation[encoding="application/x-tex"]')
    if (ann) return (ann.textContent || '').trim()
    return this._mathmlToLatex(mathEl)
  }

  private _scanFallback(): number {
    const containers = document.querySelectorAll('mjx-container')
    let count = 0
    containers.forEach((el) => {
      el.setAttribute('data-musk-formula', 'true')
      if (!el.hasAttribute('data-musk-element-id')) {
        el.setAttribute('data-musk-element-id',
          `musk-edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`)
      }
      const isDisplay = el.hasAttribute('display') || el.getAttribute('display') === 'true'
      el.setAttribute('data-musk-display', String(isDisplay))
      if (!el.getAttribute('data-musk-latex')) {
        const latex = this._extractLatex(el)
        if (latex) el.setAttribute('data-musk-latex', latex)
      }
      count++
    })
    return count
  }

  findFormulaElement(target: Element): Element | null {
    return target.closest('mjx-container[data-musk-formula]')
      || target.closest('mjx-container')
      || target.closest('.MathJax')
  }

  private async _loadMathLive(): Promise<boolean> {
    if (this.mathLiveLoaded) return true
    if (this.mathLiveLoading) {
      return new Promise<boolean>((resolve) => {
        const check = setInterval(() => {
          if (this.mathLiveLoaded) { clearInterval(check); resolve(true) }
        }, 100)
        setTimeout(() => { clearInterval(check); resolve(false) }, 15000)
      })
    }
    this.mathLiveLoading = true
    log('开始加载 MathLive CDN...')
    try {
      const mathliveModule = await (import(CONFIG.MATHLIVE_CDN) as any)
      this._applyChineseLocale(mathliveModule)
      this.mathLiveLoaded = true
      this.mathLiveLoading = false
      log('MathLive 加载成功')
      return true
    } catch (e) {
      log('MathLive 加载失败', e)
      this.mathLiveLoading = false
      return false
    }
  }

  /**
   * 注入简体中文翻译并切换 locale。
   *
   * MathLive 官方未内置中文，需通过 MathfieldElement.strings 自定义。
   * 模块导出与 window 全局都可能存在（取决于打包形式），优先使用模块导出。
   */
  private _applyChineseLocale(mathliveModule: any): void {
    try {
      const MFE = mathliveModule?.MathfieldElement || (window as any).MathfieldElement
      if (!MFE) {
        log('未找到 MathfieldElement,跳过本地化')
        return
      }
      const existing = MFE.strings || {}
      MFE.strings = { ...existing, 'zh-Hans': MATHLIVE_ZH_HANS_STRINGS }
      MFE.locale = 'zh-Hans'
      log('MathLive 已设置为简体中文')
    } catch (e) {
      log('MathLive 本地化设置失败', e)
    }
  }

  private _createPanel(): void {
    if (this.panel) return
    const el = document.createElement('div')
    el.setAttribute(CONFIG.TOOLBAR_ATTR, 'true')
    el.className = 'musk-formula-panel'

    const toolbarCard = document.createElement('div')
    toolbarCard.className = 'mfp-card mfp-toolbar-card'
    toolbarCard.innerHTML = [
      '<div class="mfp-buttons-row">',
      '  <button class="mfp-btn mfp-edit-trigger" type="button">',
      `    ${ICONS.FORMULA_EDIT}`,
      '    <span class="mfp-edit-label">编辑公式</span>',
      '  </button>',
      '  <div class="mfp-divider"></div>',
      '  <div class="mfp-ai-section">',
      `    <span class="musk-ai-label">${ICONS.AI_SPARKLE}</span>`,
      '    <input type="text" class="musk-ai-input" placeholder="描述如何修改公式...">',
      `    <button class="musk-ai-send" type="button" disabled>${ICONS.SEND_ARROW}</button>`,
      '  </div>',
      '</div>',
    ].join('\n')
    el.appendChild(toolbarCard)

    const resultCard = document.createElement('div')
    resultCard.className = 'mfp-card mfp-result-card'
    resultCard.style.display = 'none'
    resultCard.innerHTML = [
      '<div class="mfp-formula-preview"></div>',
      '<div class="mfp-result-actions">',
      `  <button class="mfp-action-btn mfp-formula-regenerate" type="button">${ICONS.RETRY}<span>重试</span></button>`,
      `  <button class="mfp-action-btn mfp-formula-apply" type="button">${ICONS.REPLACE}<span>替换</span></button>`,
      `  <button class="mfp-action-btn mfp-formula-discard" type="button">${ICONS.TRASH}<span>弃用</span></button>`,
      '</div>',
    ].join('\n')
    el.appendChild(resultCard)

    const manualCard = document.createElement('div')
    manualCard.className = 'mfp-card mfp-manual-card'
    manualCard.style.display = 'none'
    manualCard.innerHTML = [
      '<div class="mfp-manual-row">',
      '  <div class="mfp-mathfield-wrap">',
      '    <div class="mfp-loading">加载公式编辑器中...</div>',
      '  </div>',
      '  <button class="mfp-icon-btn mfp-vk-btn" type="button" data-tooltip="键盘" aria-label="键盘">',
      `    ${ICONS.KEYBOARD}`,
      '  </button>',
      '  <button class="mfp-icon-btn mfp-menu-btn" type="button" data-tooltip="插入公式" aria-label="插入公式">',
      `    ${ICONS.INSERT_FORMULA}`,
      '  </button>',
      '  <button class="mfp-confirm-btn" type="button">确定</button>',
      '</div>',
    ].join('\n')
    el.appendChild(manualCard)

    document.body.appendChild(el)
    this.panel = el
    this._currentMode = 'default'
    this._originalLatex = ''
    this._pendingFormulaContent = null
    this._lastFormulaPrompt = ''
    this._bindPanelEvents()
    this._setupScrollHandler()
  }

  private _switchMode(mode: FormulaMode): void {
    if (!this.panel) return
    this._currentMode = mode
    const toolbarCard = this.panel.querySelector('.mfp-toolbar-card') as HTMLElement
    const resultCard = this.panel.querySelector('.mfp-result-card') as HTMLElement
    const manualCard = this.panel.querySelector('.mfp-manual-card') as HTMLElement

    if (mode === 'default') {
      toolbarCard.style.display = 'block'
      manualCard.style.display = 'none'
      // result-card 由 _showResultCard / _hideResultCard 单独控制
      this._hideVK()
    } else {
      toolbarCard.style.display = 'none'
      resultCard.style.display = 'none'
      manualCard.style.display = 'block'
      this._pendingFormulaContent = null
      const self = this
      this._ensureMathField().then((ready) => {
        if (!ready || !self._mathField || !self.activeElement) return
        const lat = self.activeElement.getAttribute('data-musk-latex') || '';
        (self._mathField as any).setValue(lat, { silenceNotifications: true })
        setTimeout(() => {
          if (self._mathField) (self._mathField as any).focus()
          self._updatePosition()
        }, 50)
      })
    }
    this._updatePosition()
  }

  private async _renderPreview(latex: string): Promise<void> {
    const preview = this.panel && this.panel.querySelector('.mfp-formula-preview')
    if (!preview) return
    if (!latex) { preview.innerHTML = '<span style="color:#bbb">无公式</span>'; return }
    if (!this.mathJaxReady) { preview.textContent = latex; return }
    const displayMode = this.activeElement && this.activeElement.getAttribute('data-musk-display') === 'true'
    const delim = displayMode ? ['\\[', '\\]'] : ['\\(', '\\)']
    preview.innerHTML = delim[0] + latex + delim[1]
    try { await MathJax.typesetPromise([preview]) } catch (e) { preview.textContent = latex }
    this._updatePosition()
  }

  private _showResultCard(): void {
    const card = this.panel?.querySelector('.mfp-result-card') as HTMLElement | null
    if (card) card.style.display = 'block'
  }

  private _hideResultCard(): void {
    const card = this.panel?.querySelector('.mfp-result-card') as HTMLElement | null
    if (card) card.style.display = 'none'
    this._pendingFormulaContent = null
    this._updatePosition()
  }

  private _clearAiInput(): void {
    const aiInput = this.panel?.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.panel?.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (aiInput) { aiInput.value = ''; aiInput.disabled = false }
    if (aiSend) { aiSend.disabled = true; aiSend.classList.remove('musk-ai-loading') }
  }

  private _toggleVK(): void {
    const vk = (window as any).mathVirtualKeyboard
    if (!vk) return
    if (this._vkOpen) {
      this._hideVK()
    } else {
      if (this._mathField) (this._mathField as any).focus()
      vk.show()
      this._vkOpen = true
      this._syncVKBtnState()
      this._updatePosition()
      setTimeout(() => {
        if (this._vkOpen && this.isVisible_) this._updatePosition()
      }, KEYBOARD_REPOSITION_DELAY_MS)
    }
  }

  private _hideVK(): void {
    const vk = (window as any).mathVirtualKeyboard
    if (vk) vk.hide()
    this._vkOpen = false
    this._syncVKBtnState()
    this._updatePosition()
  }

  private _syncVKBtnState(): void {
    const btn = this.panel?.querySelector('.mfp-vk-btn') as HTMLElement | null
    if (!btn) return
    btn.classList.toggle('mfp-icon-btn-active', this._vkOpen)
  }

  /**
   * 程序化打开 MathLive 原生上下文菜单（包含插入矩阵/插入/剪切/复制/粘贴/全选）。
   * 等价于在 math-field 上长按或右键。需要 MathLive >= 0.98.1 提供的 showMenu API。
   */
  private async _openMenu(triggerBtn: HTMLElement): Promise<void> {
    const ready = await this._ensureMathField()
    if (!ready || !this._mathField) return
    const mf = this._mathField as any
    if (typeof mf.showMenu !== 'function') {
      log('MathLive showMenu API 不可用，可能版本过低')
      return
    }
    try { mf.focus() } catch (_) { /* noop */ }
    const rect = triggerBtn.getBoundingClientRect()
    try {
      mf.showMenu({
        location: { x: rect.left + rect.width / 2, y: rect.bottom + 4 },
        modifiers: { alt: false, control: false, shift: false, meta: false },
      })
    } catch (e) {
      log('showMenu 调用失败', e)
    }
  }

  private _doFormulaAiSend(): void {
    const aiInput = this.panel?.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.panel?.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (!aiInput) return
    const text = aiInput.value.trim()
    if (!text || !this.activeElement) return
    const latex = this.activeElement.getAttribute('data-musk-latex') || ''
    const elementId = this.activeElement.getAttribute('data-musk-element-id') || ''
    this._lastFormulaPrompt = text
    this._hideResultCard()
    if (aiSend) { aiSend.disabled = true; aiSend.classList.add('musk-ai-loading') }
    aiInput.disabled = true
    this._updatePosition()
    this._pendingAiTargetElementId = elementId
    this.bridge.send('AI_POLISH_TEXT', { mode: 'formula', elementId, content: latex, prompt: text })
  }

  private _setupScrollHandler(): void {
    const self = this
    this._scrollHandler = function (): void {
      if (self.isVisible_ && self.activeElement) self._updatePosition()
    }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
  }

  private _bindPanelEvents(): void {
    const self = this
    this.panel!.addEventListener('mousedown', (e: MouseEvent) => {
      if (!isMathLiveEvent(e)) e.stopPropagation()
    }, true)
    this.panel!.addEventListener('pointerdown', (e: PointerEvent) => {
      if (!isMathLiveEvent(e)) e.stopPropagation()
    }, true)
    this.panel!.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (isMathLiveEvent(e)) return
      e.stopPropagation()

      if (target.closest('.mfp-edit-trigger')) {
        e.preventDefault()
        self._switchMode('manual')
        return
      }
      if (target.closest('.mfp-formula-regenerate')) {
        e.preventDefault()
        self._hideResultCard()
        self._doFormulaAiSend()
        return
      }
      if (target.closest('.mfp-formula-apply')) {
        e.preventDefault()
        self._handleAIApply()
        return
      }
      if (target.closest('.mfp-formula-discard')) {
        e.preventDefault()
        self._hideResultCard()
        self._clearAiInput()
        return
      }
      if (target.closest('.mfp-vk-btn')) {
        e.preventDefault()
        self._toggleVK()
        return
      }
      const menuBtn = target.closest('.mfp-menu-btn') as HTMLElement | null
      if (menuBtn) {
        e.preventDefault()
        self._hideVK()
        self._openMenu(menuBtn)
        return
      }
      if (target.closest('.mfp-confirm-btn')) {
        e.preventDefault()
        self._handleInsert()
        return
      }
      const closestSend = target.closest('.musk-ai-send') as HTMLButtonElement | null
      if (closestSend && !closestSend.disabled) {
        e.preventDefault()
        self._doFormulaAiSend()
      }
    }, true)

    const aiInput = this.panel!.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.panel!.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (aiInput) {
      aiInput.addEventListener('keydown', (e: KeyboardEvent) => {
        e.stopPropagation()
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); self._doFormulaAiSend() }
        if (e.key === 'Escape') { e.preventDefault(); self.hide(false) }
      })
      aiInput.addEventListener('input', () => { if (aiSend) aiSend.disabled = !aiInput.value.trim() })
    }
  }

  private async _ensureMathField(): Promise<boolean> {
    if (this._mathField) return true
    const loaded = await this._loadMathLive()
    if (!loaded) return false
    const wrap = this.panel!.querySelector('.mfp-mathfield-wrap')!
    wrap.innerHTML = ''
    const mf = document.createElement('math-field')
    mf.setAttribute('math-virtual-keyboard-policy', 'sandboxed')
    mf.setAttribute('virtual-keyboard-mode', 'manual')
    mf.setAttribute('smart-mode', 'true')
    wrap.appendChild(mf)
    try {
      const items = (mf as any).menuItems
      if (Array.isArray(items)) {
        // 屏蔽 MathLive 上下文菜单中的：模式/字体变体/颜色/背景色，
        // 以及剪切/复制/粘贴/全选（项目内统一通过键盘快捷键处理，不暴露面板入口）。
        const removeIds = new Set([
          'mode', 'variant', 'color', 'background-color',
          'cut', 'copy', 'paste', 'select-all',
        ]);
        (mf as any).menuItems = items.filter((item: any) => !removeIds.has(item.id))
      }
    } catch (err) {
      log('MathLive menuItems 不可用，忽略', err)
    }
    this._mathField = mf
    const self = this
    mf.addEventListener('keydown', (e: KeyboardEvent) => {
      e.stopPropagation()
      // Ctrl/Cmd+Enter：提交并关闭
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); self._handleInsert() }
      // Esc：取消编辑（不提交）
      if (e.key === 'Escape') { e.preventDefault(); self.hide(false) }
    })
    if ((window as any).mathVirtualKeyboard) {
      (window as any).mathVirtualKeyboard.addEventListener('geometrychange', () => {
        const vk = (window as any).mathVirtualKeyboard
        const rect = vk && vk.boundingRect
        self._vkOpen = !!(rect && rect.height > 0)
        self._syncVKBtnState()
        if (self.isVisible_) self._updatePosition()
      })
    }
    return true
  }

  async show(formulaElement: HTMLElement, _ctx?: PluginContext): Promise<void> {
    if (!this.featureConfig.formulaEdit) return
    if (!this.panel) this._createPanel()

    const newElementId = formulaElement.getAttribute('data-musk-element-id') || ''
    if (this._pendingAiTargetElementId && this._pendingAiTargetElementId !== newElementId) {
      log('FormulaPanel 切换到其他元素，作废 pending AI 请求', {
        was: this._pendingAiTargetElementId,
        now: newElementId,
      })
      this._pendingAiTargetElementId = null
    }
    if (this.activeElement && this.activeElement !== formulaElement) {
      this.activeElement.classList.remove('musk-formula-editing')
    }
    this.activeElement = formulaElement
    formulaElement.classList.add('musk-formula-editing')
    formulaElement.classList.remove('musk-formula-hover')

    // 补全 musk 属性（与 scanFormulas / _scanFallback 行为对齐）
    // 必要场景：页面在 scanFormulas 之后才插入的 mjx-container（动态渲染、AI 优化注入等），
    // 没有 data-musk-element-id / display / formula 标记。
    // 缺 element-id 会导致 history 操作 / EDIT_END 用空 id；缺 display 会让重渲染降级为 inline。
    if (!formulaElement.hasAttribute('data-musk-formula')) {
      formulaElement.setAttribute('data-musk-formula', 'true')
    }
    if (!formulaElement.hasAttribute('data-musk-element-id')) {
      formulaElement.setAttribute('data-musk-element-id',
        `musk-edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`)
    }
    if (!formulaElement.hasAttribute('data-musk-display')) {
      // mjx-container 上的 display 属性即可判断是 block 公式还是 inline 公式
      const isDisplay = formulaElement.getAttribute('display') === 'true'
      formulaElement.setAttribute('data-musk-display', String(isDisplay))
    }

    let latex = formulaElement.getAttribute('data-musk-latex') || ''
    if (!latex) {
      latex = this._extractLatex(formulaElement)
      if (latex) formulaElement.setAttribute('data-musk-latex', latex)
    }
    this._originalLatex = latex
    this.panel!.style.display = 'block'
    this.isVisible_ = true
    this._pendingFormulaContent = null
    this._lastFormulaPrompt = ''
    this._hideResultCard()
    this._clearAiInput()
    this._switchMode('default')
    this._updatePosition()
  }

  hide(commitManual = true): void {
    if (!this.panel) return
    log('[Formula Debug] FormulaManager.hide 被调用', {
      wasVisible: this.isVisible_,
      hasActiveElement: !!this.activeElement,
      mode: this._currentMode,
      commitManual,
    })
    // 手动编辑模式下，关闭面板时自动提交（与图片面板一致）
    if (commitManual && this._currentMode === 'manual' && this._mathField && this.activeElement) {
      const newLatex = ((this._mathField as any).value || '').trim()
      if (newLatex && newLatex !== this._originalLatex) {
        // fire and forget；_applyLatex 内部会异步 typeset 并替换 DOM
        this._applyLatex(newLatex).catch((err) => log('hide 时自动提交公式失败', err))
      }
    }
    this.panel.style.display = 'none'
    this.isVisible_ = false
    this._hideVK()
    this._pendingFormulaContent = null
    this._pendingAiTargetElementId = null
    this._hideResultCard()
    this._clearAiInput()
    if (this.activeElement) {
      this.activeElement.classList.remove('musk-formula-editing')
      this.activeElement = null
    }
  }

  destroy(): void {
    if (this._scrollHandler) window.removeEventListener('scroll', this._scrollHandler)
    if (this.panel) { this.panel.remove(); this.panel = null }
    this._mathField = null
    this.activeElement = null
    this.isVisible_ = false
  }

  handleAIFormulaResult(payload: AIFormulaResultPayload | null): void {
    if (!this.panel) return
    const expected = this._pendingAiTargetElementId
    this._pendingAiTargetElementId = null
    const currentElementId = this.activeElement?.getAttribute('data-musk-element-id') || null

    const aiInput = this.panel.querySelector('.musk-ai-input') as HTMLInputElement | null
    const aiSend = this.panel.querySelector('.musk-ai-send') as HTMLButtonElement | null
    if (aiSend) aiSend.classList.remove('musk-ai-loading')
    if (aiInput) aiInput.disabled = false
    if (aiSend) aiSend.disabled = !(aiInput && aiInput.value.trim())

    if (!expected) {
      log('AI 公式结果已过期（面板已切换/关闭），丢弃')
      this._hideResultCard()
      return
    }
    if (payload?.elementId && payload.elementId !== expected) {
      log('AI 公式结果 elementId 与请求不一致，丢弃', {
        expected,
        got: payload.elementId,
      })
      this._hideResultCard()
      return
    }
    if (currentElementId !== expected) {
      log('AI 公式结果与当前 activeElement 不一致，丢弃', {
        expected,
        current: currentElementId,
      })
      this._hideResultCard()
      return
    }
    if (!payload || !payload.success || !payload.content) {
      this._hideResultCard()
      return
    }
    this._pendingFormulaContent = payload.content
    this._renderPreview(payload.content)
    this._showResultCard()
    this._updatePosition()
  }

  private async _handleInsert(): Promise<void> {
    if (!this._mathField) return
    const newLatex = (this._mathField as any).value.trim()
    await this._applyLatex(newLatex)
    this.hide(false)
  }

  private async _handleAIApply(): Promise<void> {
    if (!this._pendingFormulaContent) return
    const newLatex = this._pendingFormulaContent
    await this._applyLatex(newLatex)
    this._originalLatex = newLatex
    this._pendingFormulaContent = null
    this._hideResultCard()
    this._clearAiInput()
    this._updatePosition()
  }

  private _getAvailableHeight(): number {
    const kbRect = (window as any).mathVirtualKeyboard && (window as any).mathVirtualKeyboard.boundingRect
    if (kbRect && kbRect.height > 0) {
      const keyboardTop = typeof kbRect.y === 'number' ? kbRect.y : window.innerHeight - kbRect.height
      return Math.max(PANEL_VIEWPORT_MARGIN, keyboardTop - FORMULA_KEYBOARD_GAP)
    }
    const kbEl = document.querySelector('math-virtual-keyboard') as HTMLElement | null
    if (kbEl && kbEl.offsetHeight > 0) {
      return Math.max(PANEL_VIEWPORT_MARGIN, kbEl.getBoundingClientRect().top - FORMULA_KEYBOARD_GAP)
    }
    return window.innerHeight
  }

  private _updatePosition(): void {
    if (!this.activeElement || !this.panel) return
    const rect = this.activeElement.getBoundingClientRect()
    const availH = this._getAvailableHeight()
    const maxPanelH = Math.max(80, availH - PANEL_VIEWPORT_MARGIN * 2)
    this.panel.style.maxHeight = `${maxPanelH}px`
    this.panel.style.overflowY = 'auto'

    const panelH = Math.min(this.panel.offsetHeight, maxPanelH)
    const panelW = this.panel.offsetWidth
    const gap = PANEL_VIEWPORT_MARGIN

    let top = rect.bottom + gap
    if (top + panelH > availH - PANEL_VIEWPORT_MARGIN) top = rect.top - panelH - gap
    if (top < PANEL_VIEWPORT_MARGIN) top = Math.max(PANEL_VIEWPORT_MARGIN, availH - panelH - PANEL_VIEWPORT_MARGIN)

    let left = rect.left + (rect.width - panelW) / 2
    if (left + panelW > window.innerWidth - PANEL_VIEWPORT_MARGIN) left = window.innerWidth - panelW - PANEL_VIEWPORT_MARGIN
    if (left < PANEL_VIEWPORT_MARGIN) left = PANEL_VIEWPORT_MARGIN

    this.panel.style.top = `${top}px`
    this.panel.style.left = `${left}px`
  }

  private async _applyLatex(newLatex: string): Promise<boolean> {
    if (!this.activeElement) return false
    if (!this.mathJaxReady) return false
    newLatex = (newLatex || '').trim()
    const oldLatex = this.activeElement.getAttribute('data-musk-latex') || ''
    if (!newLatex || newLatex === oldLatex) return false
    // 兜底：show() 已补全 element-id；这里再守一次，避免极端场景下 newContainer 没有标识
    let elementId = this.activeElement.getAttribute('data-musk-element-id')
    if (!elementId) {
      elementId = `musk-edit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }
    const displayMode = this.activeElement.getAttribute('data-musk-display') === 'true'
    const oldHtml = this.activeElement.outerHTML
    const delim = displayMode ? ['\\[', '\\]'] : ['\\(', '\\)']
    const tempDiv = document.createElement('div')
    tempDiv.style.cssText = 'position:absolute;visibility:hidden;left:-9999px'
    tempDiv.innerHTML = delim[0] + newLatex + delim[1]
    document.body.appendChild(tempDiv)
    try {
      await MathJax.typesetPromise([tempDiv])
      const newContainer = tempDiv.querySelector('mjx-container')
      if (newContainer) {
        newContainer.setAttribute('data-musk-formula', 'true')
        newContainer.setAttribute('data-musk-latex', newLatex)
        newContainer.setAttribute('data-musk-display', String(displayMode))
        newContainer.setAttribute('data-musk-element-id', elementId)
        const newHtml = newContainer.outerHTML
        this.activeElement.parentNode!.replaceChild(newContainer, this.activeElement)
        this.history.addOperation({
          type: 'formula',
          elementId: elementId || '',
          oldHtml,
          newHtml,
        })
        this.bridge.send('EDIT_END', {
          elementId: elementId || '',
          changed: true,
          formulaChange: true,
          originalContent: oldHtml,
          newContent: newHtml,
        })
        this.bridge.send('HAS_CHANGES_UPDATED', { hasChanges: true })
        this.activeElement = newContainer as HTMLElement
        return true
      }
      // typesetPromise 没抛错但也没渲染出 mjx-container：通常是 LaTeX 语法不被识别
      log('公式渲染未生成 mjx-container', { newLatex })
      this.bridge.send('SHOW_TOAST', { type: 'error', message: '公式渲染失败，请检查 LaTeX 语法' })
    } catch (e) {
      log('公式渲染失败', e)
      this.bridge.send('SHOW_TOAST', { type: 'error', message: '公式渲染失败，请检查 LaTeX 语法' })
    } finally {
      tempDiv.remove()
    }
    return false
  }
}
